import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { MapPin, Search, X as XIcon, Home } from "lucide-react";
import {
    checkCartAddressLocation,
    placeOrderFromCart,
    fetchCustomerAddresses,
} from "../../service/api/orders";

const DEFAULT_ZOOM = 15;
const DEBOUNCE_MS = 600;
const SEARCH_DEBOUNCE_MS = 350;

const pinSvg = `
<svg xmlns="http://www.w3.org/2000/svg" width="40" height="50" viewBox="0 0 36 44">
  <filter id="dsa2"><feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-opacity="0.25"/></filter>
  <ellipse cx="18" cy="41" rx="7" ry="3" fill="rgba(0,0,0,0.15)"/>
  <path d="M18 2C10.3 2 4 8.3 4 16c0 10.5 14 26 14 26S32 26.5 32 16C32 8.3 25.7 2 18 2z" fill="#6366f1" filter="url(#dsa2)"/>
  <circle cx="18" cy="16" r="7.5" fill="white"/>
  <circle cx="18" cy="14" r="3" fill="#6366f1"/>
  <path d="M11 22c0-3.5 3.1-5 7-5s7 1.5 7 5" fill="#6366f1"/>
</svg>`;

const dragPinIcon = L.divIcon({ html: pinSvg, className: "", iconSize: [40, 50], iconAnchor: [20, 50] });
const fmt = (v) => `₹${Number(v ?? 0).toFixed(2)}`;

export default function PlaceOrderModal({ cart, onClose, onPlaced, showToast }) {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markerRef = useRef(null);
    const debounceRef = useRef(null);
    const requestSeq = useRef(0);
    const searchDebounceRef = useRef(null);
    const searchAbortRef = useRef(null);
    const searchWrapRef = useRef(null);

    const initialLat = Number(cart?.restaurant?.latitude) || 16.4305;
    const initialLng = Number(cart?.restaurant?.longitude) || 80.5623;

    const [mode, setMode] = useState("saved"); // "saved" | "map"
    const [savedAddresses, setSavedAddresses] = useState([]);
    const [loadingAddresses, setLoadingAddresses] = useState(true);
    const [selectedSavedId, setSelectedSavedId] = useState(null);

    const [coords, setCoords] = useState({ lat: initialLat, lng: initialLng });
    const [addressText, setAddressText] = useState("");
    const [checking, setChecking] = useState(false);
    const [placing, setPlacing] = useState(false);
    const [locationInfo, setLocationInfo] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [fastExpressPicked, setFastExpressPicked] = useState(false);

    const [searchQuery, setSearchQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [searching, setSearching] = useState(false);
    const [suggestionsOpen, setSuggestionsOpen] = useState(false);

    // ── Load the customer's saved addresses, checked against this restaurant ──
    useEffect(() => {
        if (!cart?.user?.id) return;
        setLoadingAddresses(true);
        fetchCustomerAddresses(cart.user.id, cart.restaurant?.id)
            .then((data) => setSavedAddresses(Array.isArray(data) ? data : []))
            .catch(() => setSavedAddresses([]))
            .finally(() => setLoadingAddresses(false));
    }, [cart?.user?.id, cart?.restaurant?.id]);

    // ── Outside click closes suggestion dropdown ──
    useEffect(() => {
        const handler = (e) => {
            if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
                setSuggestionsOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    // ── Address search (Nominatim), only relevant in "map" mode ──
    useEffect(() => {
        if (mode !== "map") return;
        clearTimeout(searchDebounceRef.current);
        if (searchAbortRef.current) searchAbortRef.current.abort();

        const q = searchQuery.trim();
        if (!q || q.length < 3) {
            setSuggestions([]);
            setSuggestionsOpen(false);
            setSearching(false);
            return;
        }

        setSearching(true);
        searchDebounceRef.current = setTimeout(async () => {
            const controller = new AbortController();
            searchAbortRef.current = controller;
            try {
                const rLat = Number(cart?.restaurant?.latitude);
                const rLng = Number(cart?.restaurant?.longitude);
                const params = new URLSearchParams({
                    q, format: "jsonv2", addressdetails: "1", limit: "8", countrycodes: "in",
                });
                if (Number.isFinite(rLat) && Number.isFinite(rLng)) {
                    const delta = 0.45;
                    params.set("viewbox", [rLng - delta, rLat + delta, rLng + delta, rLat - delta].join(","));
                    params.set("bounded", "0");
                }
                const res = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
                    signal: controller.signal,
                    headers: { Accept: "application/json" },
                });
                const data = await res.json();
                setSuggestions((data || []).map((item) => ({
                    id: item.place_id,
                    lat: Number(item.lat),
                    lng: Number(item.lon),
                    main_text: item.name || item.address?.road || item.address?.suburb || item.display_name,
                    secondary_text: item.display_name,
                    description: item.display_name,
                })));
                setSuggestionsOpen(true);
            } catch (err) {
                if (err.name !== "AbortError") { setSuggestions([]); setSuggestionsOpen(false); }
            } finally {
                setSearching(false);
            }
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(searchDebounceRef.current);
    }, [searchQuery, mode, cart?.restaurant?.latitude, cart?.restaurant?.longitude]);

    // ── Map setup, only once "map" mode is entered ──
    useEffect(() => {
        if (mode !== "map" || !mapRef.current || mapInstance.current) return;

        const map = L.map(mapRef.current).setView([coords.lat, coords.lng], DEFAULT_ZOOM);
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            attribution: "&copy; OpenStreetMap contributors",
        }).addTo(map);

        const marker = L.marker([coords.lat, coords.lng], { draggable: true, icon: dragPinIcon }).addTo(map);

        const handleMove = (lat, lng) => {
            setSelectedSlot(null);
            setFastExpressPicked(false);
            setSelectedSavedId(null);
            setCoords({ lat: Number(lat), lng: Number(lng) });
        };

        marker.on("dragend", () => { const { lat, lng } = marker.getLatLng(); handleMove(lat, lng); });
        map.on("click", (e) => { marker.setLatLng(e.latlng); handleMove(e.latlng.lat, e.latlng.lng); });

        mapInstance.current = map;
        markerRef.current = marker;

        setTimeout(() => mapInstance.current?.invalidateSize(), 100);

        return () => { map.remove(); mapInstance.current = null; markerRef.current = null; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    // ── Quote pricing whenever coords change ──
    useEffect(() => {
        if (!cart?.id) return;
        clearTimeout(debounceRef.current);

        const { lat, lng } = coords;
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
            setLocationInfo(null);
            return;
        }

        const seq = ++requestSeq.current;
        setChecking(true);

        debounceRef.current = setTimeout(async () => {
            try {
                const res = await checkCartAddressLocation(cart.id, { latitude: lat, longitude: lng });
                if (seq === requestSeq.current) setLocationInfo(res?.data);
            } catch (err) {
                if (seq === requestSeq.current) {
                    showToast?.("error", err?.response?.data?.message || "Failed to price this location.");
                    setLocationInfo(null);
                }
            } finally {
                if (seq === requestSeq.current) setChecking(false);
            }
        }, DEBOUNCE_MS);

        return () => clearTimeout(debounceRef.current);
    }, [coords.lat, coords.lng, cart?.id]);

    const handlePickSaved = (addr) => {
        setSelectedSavedId(addr.id);
        setAddressText(`${addr.house ? addr.house + ", " : ""}${addr.address}`);
        setCoords({ lat: Number(addr.latitude), lng: Number(addr.longitude) });
        setSelectedSlot(null);
        setFastExpressPicked(false);
    };

    const handleSelectSuggestion = (s) => {
        setSuggestionsOpen(false);
        setSearchQuery(s.description || "");
        setAddressText(s.description || "");
        setSelectedSavedId(null);
        setCoords({ lat: s.lat, lng: s.lng });
        if (markerRef.current) markerRef.current.setLatLng([s.lat, s.lng]);
        if (mapInstance.current) mapInstance.current.setView([s.lat, s.lng], 17);
    };

    const pricing = locationInfo?.pricing;

    const handlePlaceOrder = async (fastExpress) => {
        if (!addressText.trim()) {
            showToast?.("error", "Please choose a delivery address.");
            return;
        }
        if (!fastExpress && locationInfo?.slot_required && !selectedSlot) {
            showToast?.("error", "Please select a delivery slot.");
            return;
        }

        setPlacing(true);
        try {
            const res = await placeOrderFromCart(cart.id, {
                latitude: coords.lat,
                longitude: coords.lng,
                address: addressText.trim(),
                is_fast_express: fastExpress,
                delivery_slot_id: fastExpress ? null : selectedSlot?.id,
            });
            showToast?.("success", res?.message || `Order placed for ${cart.user?.name}.`);
            await onPlaced?.();
            onClose?.();
        } catch (err) {
            showToast?.("error", err?.response?.data?.message || "Failed to place order.");
        } finally {
            setPlacing(false);
        }
    };

    const PricingPanel = ({ label, data, isActive }) => {
        if (!data) return null;
        return (
            <div className={`addr-pricing${isActive ? " is-active" : ""}`}>
                <div className="addr-pricing__label">{label}</div>
                <div className="addr-pricing__row"><span>Delivery Charge</span><span>{fmt(data.delivery_charge)}</span></div>
                <div className="addr-pricing__row addr-pricing__row--total"><span>Order Total</span><span>{fmt(data.new_total)}</span></div>
            </div>
        );
    };

    return (
        <div className="modal-overlay addr-modal-overlay" onClick={onClose}>
            <div className="modal-box addr-modal-box" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3 className="modal-title">Place Order — {cart?.user?.name}</h3>
                    <button className="modal-close" onClick={onClose} disabled={placing}>✕</button>
                </div>

                <div className="modal-body addr-modal-body">
                    <div className="po-mode-toggle">
                        <button className={`po-mode-btn${mode === "saved" ? " is-active" : ""}`} onClick={() => setMode("saved")}>
                            <Home size={14} /> Saved Addresses
                        </button>
                        <button className={`po-mode-btn${mode === "map" ? " is-active" : ""}`} onClick={() => setMode("map")}>
                            <MapPin size={14} /> Pick on Map
                        </button>
                    </div>

                    <div className="addr-layout">
                        <div className="addr-layout__map">
                            {mode === "saved" ? (
                                <div className="po-address-list">
                                    {loadingAddresses ? (
                                        <div className="dp-loader"><span className="dp-loader-dot"/><span className="dp-loader-dot"/><span className="dp-loader-dot"/></div>
                                    ) : savedAddresses.length === 0 ? (
                                        <div className="po-empty">No saved addresses for this customer. Use "Pick on Map" instead.</div>
                                    ) : (
                                        savedAddresses.map((addr) => (
                                            <button
                                                key={addr.id}
                                                className={`po-address-card${selectedSavedId === addr.id ? " is-active" : ""}${addr.is_operational === 0 ? " is-disabled" : ""}`}
                                                onClick={() => handlePickSaved(addr)}
                                            >
                                                <div className="po-address-card__tag">{addr.tag || "Address"}</div>
                                                <div className="po-address-card__text">{addr.house ? `${addr.house}, ` : ""}{addr.address}</div>
                                                {addr.is_operational === 0 && <div className="po-address-card__warn">Outside delivery zone</div>}
                                            </button>
                                        ))
                                    )}
                                </div>
                            ) : (
                                <div className="addr-map-wrap">
                                    <div className="addr-search addr-search--over-map" ref={searchWrapRef}>
                                        <div className="addr-search__input-wrap">
                                            <Search size={15} className="addr-search__icon"/>
                                            <input
                                                type="text"
                                                className="addr-search__input"
                                                placeholder="Search for an address or place…"
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                onFocus={() => suggestions.length && setSuggestionsOpen(true)}
                                            />
                                            {searching && (
                                                <div className="dp-loader addr-search__loader">
                                                    <span className="dp-loader-dot"/><span className="dp-loader-dot"/><span className="dp-loader-dot"/>
                                                </div>
                                            )}
                                            {!searching && searchQuery && (
                                                <button type="button" className="addr-search__clear" onClick={() => setSearchQuery("")}>
                                                    <XIcon size={14}/>
                                                </button>
                                            )}
                                        </div>
                                        {suggestionsOpen && suggestions.length > 0 && (
                                            <div className="addr-search__dropdown">
                                                {suggestions.map((s) => (
                                                    <button type="button" key={s.id} className="addr-search__suggestion" onClick={() => handleSelectSuggestion(s)}>
                                                        <MapPin size={13} className="addr-search__suggestion-icon" />
                                                        <span className="addr-search__suggestion-text">
                                                            <span className="addr-search__suggestion-main">{s.main_text}</span>
                                                            {s.secondary_text && <span className="addr-search__suggestion-secondary">{s.secondary_text}</span>}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div ref={mapRef} className="addr-map"/>
                                </div>
                            )}

                            <textarea
                                className="modal-textarea"
                                placeholder="Full delivery address…"
                                rows={2}
                                value={addressText}
                                onChange={(e) => setAddressText(e.target.value)}
                                style={{ marginTop: 12 }}
                            />
                        </div>

                        <div className="addr-layout__side">
                            <div className="po-cart-summary">
                                <div className="po-cart-summary__row"><span>Items</span><span>{cart?.total_quantity}</span></div>
                                <div className="po-cart-summary__row"><span>Subtotal</span><span>{fmt(cart?.subtotal)}</span></div>
                            </div>

                            {checking && (
                                <div className="addr-checking">
                                    <div className="dp-loader"><span className="dp-loader-dot"/><span className="dp-loader-dot"/><span className="dp-loader-dot"/></div>
                                    Calculating delivery pricing…
                                </div>
                            )}

                            {!checking && locationInfo && (
                                <>
                                    <div className="addr-result__distance"><MapPin size={14}/> {locationInfo.distance} km from restaurant</div>

                                    {locationInfo.slot_required && (
                                        <>
                                            <div className="modal-section-label" style={{ marginTop: 10 }}>Delivery slots</div>
                                            <div className="modal-reasons">
                                                {locationInfo.slots?.map((slot) => (
                                                    <button
                                                        key={slot.id}
                                                        className={`modal-reason-chip${selectedSlot?.id === slot.id && !fastExpressPicked ? " is-active" : ""}`}
                                                        onClick={() => { setSelectedSlot(slot); setFastExpressPicked(false); }}
                                                    >
                                                        {slot.name} ({slot.start_time}–{slot.end_time})
                                                    </button>
                                                ))}
                                            </div>
                                            <PricingPanel label="Slot-based delivery" data={pricing?.slot_based} isActive={!fastExpressPicked} />
                                            <button
                                                className="action-btn action-btn--accept"
                                                disabled={!selectedSlot || fastExpressPicked || placing}
                                                onClick={() => handlePlaceOrder(false)}
                                            >
                                                {placing && !fastExpressPicked ? "Placing…" : "Place Order (Slot)"}
                                            </button>
                                            <div className="addr-or">or</div>
                                        </>
                                    )}

                                    <PricingPanel label="Fast Express delivery" data={pricing?.fast_express} isActive={fastExpressPicked || !locationInfo.slot_required} />
                                    <button
                                        className="action-btn action-btn--accept"
                                        style={{ width: "100%", justifyContent: "center" }}
                                        disabled={placing}
                                        onClick={() => { setFastExpressPicked(true); handlePlaceOrder(true); }}
                                    >
                                        {placing && (fastExpressPicked || !locationInfo.slot_required) ? "Placing…" : "Place Order (Fast Express)"}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}